// src/Redux/Slice/GeminiOCR.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

// Base URL for your API
const BaseUrl = process.env.REACT_APP_API_URL;

// --- Asynchronous Thunks ---

// Fetch All Client Scan Details Thunk
export const fetchPlanData = createAsyncThunk(
    'GeminiOCR/fetchPlanData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowAllClientScanDtl`);
            return response.data?.data || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Add New Client Scan Detail Thunk
export const AddNewPlan = createAsyncThunk(
    'GeminiOCR/AddNewPlan',
    async ({ values }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/ImgToText/AddClientScanDtl`, values);

            if (response?.data.status === "Customer add") {
                toast.success("Member added successfully!");
            }
            return response.data;
        } catch (error) {
            toast.error("Something went wrong while adding member.");
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to add member"
            );
        }
    }
);

// Delete Client Scan Detail Thunk
export const DeletePlan = createAsyncThunk(
    'GeminiOCR/DeletePlan', // Changed to match what you're dispatching
    async ({ id }, { rejectWithValue }) => {
        try {
            // Use DELETE method instead of GET for delete operations
            const response = await axios.post(`${BaseUrl}/ImgToText/DeleteAllClientScanDtl/${id}`);

            // Show success message
            if (response.data?.success) {
                toast.success("Plan deleted successfully!");
            }

            return { id, ...response.data }; // Return the ID for filtering
        } catch (error) {
            toast.error("Failed to delete plan.");
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Fetch All Client Scan Details Thunk
export const fetchInwardData = createAsyncThunk(
    'GeminiOCR/fetchInwardData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowInwScanLog`);
            return response.data?.data || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// Fetch All Client Scan Details Thunk
export const fetchDeliveryData = createAsyncThunk(
    'GeminiOCR/fetchDeliveryData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowDelScanLog`);
            return response.data?.data || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);


// --- Sign Copy Receive Thunk ---
export const fetchSignCopyReceive = createAsyncThunk(
    'GeminiOCR/fetchSignCopyReceive',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowSignCopyRcvScanLog`);
            return response.data?.data || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// --- Fetch Combined Scan Data Thunk (Inward + Delivery) ---
export const fetchCombinedScanData = createAsyncThunk(
    'GeminiOCR/fetchCombinedScanData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowCombinedScanLog`);
            return response.data || {};
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// --- OCR Vendor Payment & Invoices Thunks ---
export const fetchOCRPayments = createAsyncThunk(
    'GeminiOCR/fetchOCRPayments',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/ImgToText/ShowAllOCRPayments`);
            return response.data?.data || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const addOCRPayment = createAsyncThunk(
    'GeminiOCR/addOCRPayment',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/ImgToText/AddOCRPayment`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data?.data || response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const deleteOCRPayment = createAsyncThunk(
    'GeminiOCR/deleteOCRPayment',
    async ({ id }, { rejectWithValue }) => {
        try {
            await axios.delete(`${BaseUrl}/ImgToText/DeleteOCRPayment/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

// --- Redux Slice ---

const GeminiOCR = createSlice({
    name: 'GeminiOcr',
    initialState: {
        // Plan Data State
        PlanData: [],
        planLoading: false,
        planError: null,

        // Add Plan State
        addPlanLoading: false,
        addPlanSuccess: false,
        addPlanError: null,

        // Delete Plan State
        deletePlanLoading: false,
        deletePlanSuccess: false,
        deletePlanError: null,

        // Delivery Data State (NEW)
        InwardData: [],
        inwardLoading: false,
        inwardError: null,

        // Inward Data State (NEW)
        DeliveryData: [],
        deliveryLoading: false,
        deliveryError: null,

        // Sign Copy Receive Data State (NEW)
        SignCopyReceiveData: [],
        SignCopyReceiveLoading: false,
        SignCopyReceiveError: null,

        // Combined Scan Data State
        CombinedScanData: [],
        PartyBalances: [],
        combinedLoading: false,
        combinedError: null,

        // OCR Vendor Payments State
        ocrPayments: [],
        ocrPaymentsLoading: false,
        ocrPaymentsError: null,
        addPaymentLoading: false,
        addPaymentSuccess: false,
    },
    reducers: {
        // Reset states
        resetAddPlanState: (state) => {
            state.addPlanLoading = false;
            state.addPlanSuccess = false;
            state.addPlanError = null;
        },
        resetDeletePlanState: (state) => {
            state.deletePlanLoading = false;
            state.deletePlanSuccess = false;
            state.deletePlanError = null;
        },
        resetPlanState: (state) => {
            state.planLoading = false;
            state.planError = null;
        },
        resetInwardState: (state) => {
            state.inwardLoading = false;
            state.inwardError = null;
        },
        // Manual data manipulation reducers if needed
        clearPlanData: (state) => {
            state.PlanData = [];
        },
        setPlanData: (state, action) => {
            state.PlanData = action.payload;
        },
        // Clear Inward Data (NEW)
        clearInwardData: (state) => {
            state.InwardData = [];
        },
        setInwardData: (state, action) => {
            state.InwardData = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // --- Fetch Plan Data Handlers ---
            .addCase(fetchPlanData.pending, (state) => {
                state.planLoading = true;
                state.planError = null;
            })
            .addCase(fetchPlanData.fulfilled, (state, action) => {
                state.planLoading = false;
                state.PlanData = action.payload;
            })
            .addCase(fetchPlanData.rejected, (state, action) => {
                state.planLoading = false;
                state.planError = action.payload;
            })

            // --- Add New Plan Handlers ---
            .addCase(AddNewPlan.pending, (state) => {
                state.addPlanLoading = true;
                state.addPlanSuccess = false;
                state.addPlanError = null;
            })
            .addCase(AddNewPlan.fulfilled, (state, action) => {
                state.addPlanLoading = false;
                state.addPlanSuccess = true;
                // Add new plan to the beginning of the array
                if (action.payload) {
                    state.PlanData.unshift(action.payload);
                }
            })
            .addCase(AddNewPlan.rejected, (state, action) => {
                state.addPlanLoading = false;
                state.addPlanSuccess = false;
                state.addPlanError = action.payload;
            })

            // --- Delete Plan Handlers ---
            .addCase(DeletePlan.pending, (state) => {
                state.deletePlanLoading = true;
                state.deletePlanSuccess = false;
                state.deletePlanError = null;
            })
            .addCase(DeletePlan.fulfilled, (state, action) => {
                state.deletePlanLoading = false;
                state.deletePlanSuccess = true;
                // Remove deleted plan from array - CORRECTED
                state.PlanData = state.PlanData.filter(plan => plan._id !== action.payload.id);
            })
            .addCase(DeletePlan.rejected, (state, action) => {
                state.deletePlanLoading = false;
                state.deletePlanSuccess = false;
                state.deletePlanError = action.payload;
                toast.error(`Delete failed: ${action.payload}`);
            })

            // --- Fetch Inward Data Handlers (NEW) ---
            .addCase(fetchInwardData.pending, (state) => {
                state.inwardLoading = true;
                state.inwardError = null;
            })
            .addCase(fetchInwardData.fulfilled, (state, action) => {
                state.inwardLoading = false;
                state.InwardData = action.payload;
            })
            .addCase(fetchInwardData.rejected, (state, action) => {
                state.inwardLoading = false;
                state.inwardError = action.payload;
            })

            // --- Fetch Delivery Data Handlers (NEW) ---
            .addCase(fetchDeliveryData.pending, (state) => {
                state.deliveryLoading = true;
                state.deliveryError = null;
            })
            .addCase(fetchDeliveryData.fulfilled, (state, action) => {
                state.deliveryLoading = false;
                state.DeliveryData = action.payload;
            })
            .addCase(fetchDeliveryData.rejected, (state, action) => {
                state.deliveryLoading = false;
                state.deliveryError = action.payload;
            })

            // --- Sign Copy Receive Data ---
            .addCase(fetchSignCopyReceive.pending, (state) => {
                state.SignCopyReceiveLoading = true;
                state.SignCopyReceiveError = null;
            })
            .addCase(fetchSignCopyReceive.fulfilled, (state, action) => {
                state.SignCopyReceiveLoading = false;
                state.SignCopyReceiveData = action.payload;
            })
            .addCase(fetchSignCopyReceive.rejected, (state, action) => {
                state.SignCopyReceiveLoading = false;
                state.SignCopyReceiveError = action.payload;
            })

            // --- Fetch Combined Scan Data Handlers ---
            .addCase(fetchCombinedScanData.pending, (state) => {
                state.combinedLoading = true;
                state.combinedError = null;
            })
            .addCase(fetchCombinedScanData.fulfilled, (state, action) => {
                state.combinedLoading = false;
                state.CombinedScanData = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
                state.PartyBalances = action.payload?.partyBalances || [];
            })
            .addCase(fetchCombinedScanData.rejected, (state, action) => {
                state.combinedLoading = false;
                state.combinedError = action.payload;
            })

            // --- OCR Vendor Payments Handlers ---
            .addCase(fetchOCRPayments.pending, (state) => {
                state.ocrPaymentsLoading = true;
                state.ocrPaymentsError = null;
            })
            .addCase(fetchOCRPayments.fulfilled, (state, action) => {
                state.ocrPaymentsLoading = false;
                state.ocrPayments = action.payload || [];
            })
            .addCase(fetchOCRPayments.rejected, (state, action) => {
                state.ocrPaymentsLoading = false;
                state.ocrPaymentsError = action.payload;
            })
            .addCase(addOCRPayment.pending, (state) => {
                state.addPaymentLoading = true;
                state.addPaymentSuccess = false;
            })
            .addCase(addOCRPayment.fulfilled, (state, action) => {
                state.addPaymentLoading = false;
                state.addPaymentSuccess = true;
                if (action.payload) {
                    state.ocrPayments.unshift(action.payload);
                }
            })
            .addCase(addOCRPayment.rejected, (state, action) => {
                state.addPaymentLoading = false;
                state.addPaymentSuccess = false;
            })
            .addCase(deleteOCRPayment.fulfilled, (state, action) => {
                state.ocrPayments = state.ocrPayments.filter(p => p._id !== action.payload);
            });
    }
});

// Export actions
export const {
    resetAddPlanState,
    resetDeletePlanState,
    resetPlanState,
    resetInwardState,
    clearPlanData,
    setPlanData,
    clearInwardData,
    setInwardData
} = GeminiOCR.actions;

// Export the reducer
export default GeminiOCR.reducer;